import React, { useEffect, useState } from 'react';
import { Navigate, Link, useLocation, Outlet, useNavigate } from 'react-router-dom';

export const SuperadminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('superadminToken');
    const userData = localStorage.getItem('superadminUser');
    if (!token) {
      navigate('/superadmin/login');
    } else if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [navigate]);

  const navItems = [
    { 
      label: 'Overview', 
      path: '/superadmin/dashboard', 
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v10a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4z" /></svg>
    },
    { 
      label: 'Tenants', 
      path: '/superadmin/tenants', 
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
    },
    { 
      label: 'Settings', 
      path: '/superadmin/settings', 
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
    }
  ];

  const currentNav = navItems.find(item => location.pathname.startsWith(item.path)) || navItems[0];

  const handleLogout = () => {
    localStorage.removeItem('superadminToken');
    localStorage.removeItem('superadminUser');
    navigate('/superadmin/login');
  };

  const initials = user ? `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase() : 'SA';

  const renderSidebarLinks = (onClickCallback?: () => void) => {
    return navItems.map((item) => {
      const active = location.pathname.startsWith(item.path);
      return (
        <Link
          key={item.label}
          to={item.path}
          onClick={onClickCallback}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-nav ${
            active 
              ? 'bg-indigo-600 text-white font-semibold shadow-md' 
              : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          <span className="shrink-0">{item.icon}</span>
          <span className={`transition-opacity duration-200 ${!sidebarExpanded ? 'lg:hidden' : ''}`}>
            {item.label}
          </span>
        </Link>
      );
    });
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-50 text-slate-900">
      
      {/* Desktop Sidebar Navigation */}
      <aside className={`hidden md:flex flex-col bg-slate-900 text-white shrink-0 shadow-lg border-r border-slate-800 transition-all duration-200 ${
        sidebarExpanded ? 'w-64' : 'w-20'
      }`}>
        <div className="flex items-center gap-3 h-16 px-4 border-b border-slate-800 shrink-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-500 flex items-center justify-center text-lg shadow-lg shadow-indigo-500/20">👑</div>
          <div className={`overflow-hidden transition-all duration-200 ${sidebarExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 pointer-events-none'}`}>
            <h2 className="font-bold text-xs leading-tight text-white truncate max-w-[140px]">Master Console</h2>
            <span className="text-[9px] text-indigo-400 font-bold uppercase tracking-wider">Superadmin</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1.5 scrollbar-thin scrollbar-thumb-slate-800">
          {renderSidebarLinks()}
        </nav>

        <div className="p-3 border-t border-slate-800 space-y-1">
          <button 
            onClick={() => setSidebarExpanded(!sidebarExpanded)} 
            className="w-full flex items-center justify-center py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all text-helper font-medium gap-2"
          >
            <svg className={`w-4 h-4 transition-transform duration-200 ${sidebarExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
            <span className={sidebarExpanded ? 'inline' : 'hidden'}>Collapse Sidebar</span>
          </button>
          
          <button 
            onClick={handleLogout} 
            className="w-full flex items-center py-2.5 px-3 rounded-lg text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition-all text-helper font-medium gap-2"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className={sidebarExpanded ? 'inline' : 'hidden'}>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile Drawer Overlay */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setMobileDrawerOpen(false)}>
          <aside className="w-64 max-w-[80vw] h-full bg-slate-900 text-white flex flex-col justify-between py-6 shadow-2xl animate-slide-in" onClick={e => e.stopPropagation()}>
            <div>
              <div className="flex items-center gap-3 px-5 mb-6 border-b border-slate-800 pb-5">
                <div className="w-9 h-9 rounded-lg bg-indigo-500 flex items-center justify-center text-lg shadow-lg shadow-indigo-500/20">👑</div>
                <div>
                  <h2 className="font-bold text-xs text-white leading-tight">Master Console</h2>
                  <span className="text-[9px] text-indigo-400 font-bold uppercase tracking-wider">Superadmin</span>
                </div>
              </div>
              <nav className="flex flex-col gap-1.5 px-3">
                {renderSidebarLinks(() => setMobileDrawerOpen(false))}
              </nav>
            </div>
            <div className="px-5 border-t border-slate-800 pt-5">
              <button 
                onClick={() => { setMobileDrawerOpen(false); handleLogout(); }} 
                className="w-full flex items-center gap-3 py-3 text-rose-400 font-semibold text-helper"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Sign Out</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Workspace Container */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 h-16 px-4 md:px-8 flex items-center justify-between shadow-sm shrink-0">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMobileDrawerOpen(true)} 
              className="md:hidden p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-2 text-helper font-semibold text-slate-400">
              <span className="hidden sm:inline">System Management</span>
              <span className="hidden sm:inline text-slate-300">/</span>
              <span className="text-slate-800 font-bold">{currentNav.label}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="font-bold text-helper text-slate-800 leading-tight">{user ? `${user.first_name} ${user.last_name}` : 'System Admin'}</p>
              <span className="text-[9px] text-indigo-600 font-bold uppercase tracking-wider leading-none">Global Owner</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center font-bold text-helper border-2 border-white shadow-md">
              {initials}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-50">
          <Outlet />
        </main>
      </div>

    </div>
  );
};

export default SuperadminLayout;
